import { defineStore } from 'pinia'
import { useApi } from '@/utils/axios'
import { CAMPANHAS_ROTAS } from '@/utils/apiRoutes'

export const useCampanhaMarketingStore = defineStore('campanhaMarketing', {
    state: () => ({
        loading: false,
        success: false,
        error: '',
        lastResult: null,
        templates: []
    }),
    actions: {
        async contarClientes(filters = {}, tipoCampanha = 'push') {
            this.error = ''
            try {
                // Determinar qual rota usar baseado no tipo de campanha
                let route
                if (tipoCampanha === 'whatsapp') {
                    route = CAMPANHAS_ROTAS.WHATSAPP.CONTAR
                } else if (tipoCampanha === 'sms') {
                    route = CAMPANHAS_ROTAS.SMS.CONTAR
                } else {
                    route = CAMPANHAS_ROTAS.PUSH.CONTAR
                }

                const api = useApi(route)
                const resp = await api.fetch({ params: filters })
                if (!resp || resp.status !== 'success') {
                    const msg = resp?.message || 'Erro ao contar clientes elegíveis.'
                    this.error = msg
                    throw new Error(msg)
                }
                const total = Number(resp.data?.total ?? 0)
                return total
            } catch (e) {
                this.error = e?.response?.data?.message || e.message || 'Erro ao contar clientes elegíveis.'
                throw e
            }
        },
        async enviarCampanha(payload, tipoCampanha = 'push') {
            this.loading = true
            this.error = ''
            this.success = false
            try {
                // Determinar qual rota usar baseado no tipo de campanha
                let route
                if (tipoCampanha === 'whatsapp') {
                    route = CAMPANHAS_ROTAS.WHATSAPP.ENVIAR
                } else if (tipoCampanha === 'sms') {
                    route = CAMPANHAS_ROTAS.SMS.ENVIAR
                } else {
                    route = CAMPANHAS_ROTAS.PUSH.ENVIAR
                }

                const api = useApi(route)
                const { data } = await api.post(payload)
                this.success = true
                this.lastResult = data
                return data
            } catch (e) {
                this.error = e?.response?.data?.message || `Erro ao enviar ${tipoCampanha}.`
                throw e
            } finally {
                this.loading = false
            }
        },
        // Manter método enviarPush para compatibilidade
        async enviarPush(payload) {
            return this.enviarCampanha(payload, 'push')
        },
        // Novo método para enviar WhatsApp
        async enviarWhatsapp(payload) {
            return this.enviarCampanha(payload, 'whatsapp')
        },
        // Novo método para enviar SMS
        async enviarSms(payload) {
            return this.enviarCampanha(payload, 'sms')
        },
        // Método para listar campanhas SMS
        async listarCampanhasSMS(page = 1, perPage = 15) {
            this.loading = true
            this.error = ''
            try {
                const api = useApi(CAMPANHAS_ROTAS.SMS.LISTAR)
                const resp = await api.fetch({ params: { page, per_page: perPage } })

                if (!resp || resp.status !== 'success') {
                    const msg = resp?.message || 'Erro ao listar campanhas SMS.'
                    this.error = msg
                    throw new Error(msg)
                }

                return resp.data || { data: [], pagination: {} }
            } catch (e) {
                this.error = e?.response?.data?.message || e.message || 'Erro ao listar campanhas SMS.'
                throw e
            } finally {
                this.loading = false
            }
        },
        // Método para obter detalhes de uma campanha SMS
        async obterCampanhaSMS(id) {
            this.loading = true
            this.error = ''
            try {
                const api = useApi(CAMPANHAS_ROTAS.SMS.OBTER(id))
                const resp = await api.fetch()

                if (!resp || resp.status !== 'success') {
                    const msg = resp?.message || 'Erro ao obter campanha SMS.'
                    this.error = msg
                    throw new Error(msg)
                }

                return resp.data || {}
            } catch (e) {
                this.error = e?.response?.data?.message || e.message || 'Erro ao obter campanha SMS.'
                throw e
            } finally {
                this.loading = false
            }
        },
        // Método para obter estatísticas de cliques de uma campanha
        async obterEstatisticasCliques(id) {
            this.loading = true
            this.error = ''
            try {
                const api = useApi(CAMPANHAS_ROTAS.SMS.CLICK_STATS(id))
                const resp = await api.fetch()

                if (!resp || resp.status !== 'success') {
                    const msg = resp?.message || 'Erro ao obter estatísticas de cliques.'
                    this.error = msg
                    throw new Error(msg)
                }

                return resp.data || {}
            } catch (e) {
                this.error = e?.response?.data?.message || e.message || 'Erro ao obter estatísticas de cliques.'
                throw e
            } finally {
                this.loading = false
            }
        },
        // Método para cancelar uma campanha SMS
        async cancelarCampanhaSMS(id) {
            this.loading = true
            this.error = ''
            try {
                const api = useApi(CAMPANHAS_ROTAS.SMS.CANCELAR(id))
                const resp = await api.put({})

                if (!resp || resp.status !== 'success') {
                    const msg = resp?.message || 'Erro ao cancelar campanha SMS.'
                    this.error = msg
                    throw new Error(msg)
                }

                return resp
            } catch (e) {
                this.error = e?.response?.data?.message || e.message || 'Erro ao cancelar campanha SMS.'
                throw e
            } finally {
                this.loading = false
            }
        },
        // Método para buscar templates de WhatsApp
        async buscarTemplates(provedor = 'onemsg') {
            this.loading = true
            this.error = ''
            try {
                const api = useApi(CAMPANHAS_ROTAS.WHATSAPP.TEMPLATES)
                const resp = await api.fetch({ params: { provedor } })

                if (!resp || resp.status !== 'success') {
                    const msg = resp?.message || 'Erro ao buscar templates.'
                    this.error = msg
                    throw new Error(msg)
                }

                this.templates = resp.data || []
                return this.templates
            } catch (e) {
                this.error = e?.response?.data?.message || e.message || 'Erro ao buscar templates.'
                this.templates = []
                throw e
            } finally {
                this.loading = false
            }
        },
        clearResult() {
            this.lastResult = null
            this.success = false
            this.error = ''
        }
    }
})